import { getTaskExecutionKey, type TaskState } from './task-lifecycle';

const FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  brand: 'Brand',
  bullet_one: 'First bullet',
  bullet_two: 'Second bullet',
  description: 'Description',
};

export interface TaskTerminalNotification {
  id: string;
  kind: 'success' | 'warning' | 'error' | 'info';
  title: string;
  description?: string;
}

export function getTaskTerminalNotification(task: TaskState): TaskTerminalNotification | null {
  if (task.taskType === 'thumbnail_generation') return null;
  const fieldName = task.payload?.field ?? task.result?.field;
  const field = typeof fieldName === 'string' ? FIELD_LABELS[fieldName] : undefined;
  const operation =
    task.taskType === 'design_check'
      ? 'Image check'
      : task.taskType === 'listing_check'
        ? 'Listing check'
        : task.taskType === 'field_generation'
          ? `${field ?? 'Field'} generation`
          : 'Listing generation';
  const name = task.displayName ?? 'design';
  const id = `task-result:${getTaskExecutionKey(task)}`;

  if (task.status === 'failed') {
    return {
      id,
      kind: 'error',
      title: `${operation} failed — ${name}`,
      description: task.error?.message ?? task.progressMessage,
    };
  }
  if (task.status === 'cancelled') {
    return { id, kind: 'info', title: `${operation} cancelled — ${name}` };
  }
  if (task.status !== 'completed') return null;

  if (task.taskType === 'design_check' || task.taskType === 'listing_check') {
    const outcome =
      task.taskType === 'design_check' ? task.result?.check_status : task.result?.compliance_status;
    if (outcome === 'approved')
      return { id, kind: 'success', title: `${operation} passed — ${name}` };
    if (outcome === 'warning' || outcome === 'rejected') {
      return {
        id,
        kind: 'warning',
        title: `${operation} found ${outcome === 'warning' ? 'warnings' : 'issues'} — ${name}`,
        description: 'Open the design to review the findings.',
      };
    }
    return {
      id,
      kind: 'info',
      title: `${operation} completed — ${name}`,
      description: 'Open the design to review the result.',
    };
  }

  return {
    id,
    kind: 'success',
    title: `${task.taskType === 'field_generation' ? `${field ?? 'Field'} generated` : 'Listing generated'} — ${name}`,
  };
}
